import { useState, useEffect } from "react";
import RunMigrationButton from "../RunMigrationButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/use-toast";
import {
  Brain,
  Mic,
  Speaker,
  Bot,
  Cpu,
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Phone,
  Settings,
  Zap,
} from "lucide-react";
import { providerCredentialsService } from "@/services/providerCredentialsService";
import {
  ProviderCredential,
  PROVIDER_NAMES,
  PROVIDER_INFO,
  ProviderName,
  ProviderCategory,
  PROVIDER_CATEGORIES,
  getProvidersByCategory,
} from "@/types/providerCredentials";
import { useOrganization } from "../../App";

interface ProvidersTabProps {
  organizationId: string;
}

export default function ProvidersTab() {
  const { currentOrganization } = useOrganization();
  const [credentials, setCredentials] = useState<
    ProviderCredential[] & { error?: any }
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ProviderName | null>(
    null,
  );
  const [selectedCredential, setSelectedCredential] =
    useState<ProviderCredential | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchCredentials();
    }
  }, [currentOrganization?.id]);

  const fetchCredentials = async () => {
    setIsLoading(true);
    try {
      try {
        const data = await providerCredentialsService.getProviderCredentials(
          currentOrganization!.id,
        );
        setCredentials(data);
      } catch (error) {
        console.error("Error fetching credentials:", error);
        setCredentials(Object.assign([], { error }));
        toast({
          title: "Error",
          description:
            "The provider_credentials table might not exist yet. Please run the migration.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error in outer try-catch:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch provider credentials",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (
    provider?: ProviderName,
    credential?: ProviderCredential,
  ) => {
    if (provider) {
      setSelectedProvider(provider);
      setSelectedCredential(credential || null);

      // Initialize form data
      if (credential) {
        const config = credential.config || {};
        
        // Handle special case for Plivo where auth_id is stored as api_key
        // Handle special case for Twilio where account_sid is stored as api_key
        if (provider === "Plivo") {
          setFormData({
            auth_id: credential.api_key, // Plivo's auth_id is stored in api_key field
            ...config,
          });
        } else if (provider === "Twilio") {
          setFormData({
            account_sid: credential.api_key, // Twilio's account_sid is stored in api_key field
            ...config,
          });
        } else {
          setFormData({
            api_key: credential.api_key,
            ...config,
          });
        }
      } else {
        setFormData({});
      }

      setIsDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProvider(null);
    setSelectedCredential(null);
    setFormData({});
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedProvider || !currentOrganization?.id) return;

    setIsSubmitting(true);
    try {
      // Handle special case for Plivo where auth_id is the primary key
      // Handle special case for Twilio where account_sid is the primary key
      let api_key;
      let configFields;
      
      if (selectedProvider === "Plivo") {
        api_key = formData.auth_id;
        if (!api_key) {
          toast({
            title: "Error",
            description: "Auth ID is required for Plivo",
            variant: "destructive",
          });
          return;
        }
        // For Plivo, auth_token goes in config, auth_id goes in api_key field
        const { auth_id, ...otherFields } = formData;
        configFields = otherFields;
      } else if (selectedProvider === "Twilio") {
        api_key = formData.account_sid;
        if (!api_key) {
          toast({
            title: "Error",
            description: "Account SID is required for Twilio",
            variant: "destructive",
          });
          return;
        }
        // For Twilio, auth_token and other fields go in config, account_sid goes in api_key field
        const { account_sid, ...otherFields } = formData;
        configFields = otherFields;
      } else {
        api_key = formData.api_key;
        if (!api_key) {
          toast({
            title: "Error",
            description: "API Key is required",
            variant: "destructive",
          });
          return;
        }
        // Extract config fields (all except api_key)
        const { api_key: apiKeyField, ...otherFields } = formData;
        configFields = otherFields;
      }

      if (selectedCredential) {
        // Update existing credential
        await providerCredentialsService.updateProviderCredential(
          selectedCredential.id,
          {
            api_key,
            config:
              Object.keys(configFields).length > 0 ? configFields : undefined,
          },
        );
        toast({
          title: "Success",
          description: `${selectedProvider} credentials updated successfully`,
        });
      } else {
        // Create new credential
        await providerCredentialsService.createProviderCredential({
          organization_id: currentOrganization!.id,
          provider_name: selectedProvider.toLowerCase(),
          api_key,
          is_active: true,
          config:
            Object.keys(configFields).length > 0 ? configFields : undefined,
        });
        toast({
          title: "Success",
          description: `${selectedProvider} credentials added successfully`,
        });
      }

      // Create SIP trunk for Plivo provider when credentials are saved
      if (selectedProvider === "Plivo" && configFields.caller_id && configFields.endpoint_url) {
        try {
          toast({
            description: "Creating SIP trunk with Plivo credentials...",
            variant: "default",
          });
          
          const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || 'http://localhost:8000';
          
          // Create SIP trunk using Plivo credentials
          const sipResponse = await fetch(`${CALL_SERVICE_URL}/sip/create-trunk-from-credentials`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              organization_id: currentOrganization!.id,
              phone_number: configFields.caller_id, // Use caller_id as phone number
              auth_username: api_key, // auth_id as username
              auth_password: configFields.auth_token, // auth_token as password
              endpoint_url: configFields.endpoint_url, // Use user-entered endpoint URL
            }),
          });
          
          const sipResult = await sipResponse.json();
          
          if (sipResult.success) {
            toast({
              description: `Successfully created SIP trunk for ${configFields.caller_id}`,
              variant: "default",
            });
          } else {
            toast({
              description: `SIP trunk creation warning: ${sipResult.message || 'Unknown error'}`,
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('SIP trunk creation error:', error);
          toast({
            description: "Failed to create SIP trunk. You can create it manually later.",
            variant: "destructive",
          });
        }
      }

      // Create SIP trunk for Twilio provider when credentials are saved
      if (selectedProvider === "Twilio" && configFields.phone_number && configFields.trunk_domain) {
        try {
          toast({
            description: "Creating SIP trunk with Twilio credentials...",
            variant: "default",
          });
          
          const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || 'http://localhost:8000';
          
          // Create SIP trunk using Twilio credentials
          const sipResponse = await fetch(`${CALL_SERVICE_URL}/sip/create-twilio-trunk-from-credentials`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              organization_id: currentOrganization!.id,
              phone_number: configFields.phone_number,
              account_sid: api_key, // account_sid is stored as api_key for Twilio
              auth_token: configFields.auth_token,
              trunk_domain: configFields.trunk_domain,
            }),
          });
          
          const sipResult = await sipResponse.json();
          
          if (sipResult.success) {
            toast({
              description: `Successfully created SIP trunk for ${configFields.phone_number}`,
              variant: "default",
            });
          } else {
            toast({
              description: `SIP trunk creation warning: ${sipResult.message || 'Unknown error'}`,
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Twilio SIP trunk creation error:', error);
          toast({
            description: "Failed to create Twilio SIP trunk. You can create it manually later.",
            variant: "destructive",
          });
        }
      }

      handleCloseDialog();
      fetchCredentials();
    } catch (error: any) {
      console.error("Error saving credentials:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save provider credentials",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (credential: ProviderCredential) => {
    try {
      await providerCredentialsService.toggleProviderCredentialStatus(
        credential.id,
        !credential.is_active,
      );
      toast({
        title: "Success",
        description: `${credential.provider_name} ${credential.is_active ? "disabled" : "enabled"} successfully`,
      });
      fetchCredentials();
    } catch (error: any) {
      console.error("Error toggling status:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (credential: ProviderCredential) => {
    try {
      await providerCredentialsService.deleteProviderCredential(credential.id);
      toast({
        title: "Success",
        description: `${credential.provider_name} credentials deleted successfully`,
      });
      fetchCredentials();
    } catch (error: any) {
      console.error("Error deleting credentials:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete provider credentials",
        variant: "destructive",
      });
    }
  };

  const getProviderIcon = (providerName: string) => {
    switch (providerName) {
      case "OpenAI":
      case "Anthropic":
        return <Brain className="h-6 w-6" />;
      case "DeepGram":
        return <Mic className="h-6 w-6" />;
      case "ElevenLabs":
        return <Speaker className="h-6 w-6" />;
      case "GROQ":
        return <Bot className="h-6 w-6" />;
      case "SmallestAI":
        return <Cpu className="h-6 w-6" />;
      case "LiveKit":
      case "Plivo":
        return <Phone className="h-6 w-6" />;
      case "OpenAIRealtime":
      case "CustomRealtime":
        return <Zap className="h-6 w-6" />;
      case "Mock":
        return <Settings className="h-6 w-6" />;
      default:
        return <Brain className="h-6 w-6" />;
    }
  };

  const getExistingCredential = (providerName: string) => {
    console.log('Looking for provider:', providerName);
    console.log('Available credentials:', credentials.map(c => c.provider_name));
    const found = credentials.find((cred) => cred.provider_name === providerName.toLowerCase());
    console.log('Found credential:', found);
    return found;
  };

  console.log("ProvidersTab pre-render, isLoading:", isLoading);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if we got a specific error about the table not existing
  const tableNotExistError =
    credentials.length === 0 &&
    credentials.error?.code === "42P01" &&
    credentials.error?.message?.includes(
      'relation "public.provider_credentials" does not exist',
    );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        {credentials.error?.code === "42P01" && <RunMigrationButton />}
      </div>

      {credentials.error?.code === "42P01" && (
        <div className="bg-mid-bg border border-mid-line rounded-md p-4 mb-6">
          <h3 className="text-mid font-medium">Database Table Missing</h3>
          <p className="text-mid mt-1">
            The provider_credentials table doesn't exist in your database yet.
            Click the "Run Provider Credentials Migration" button to create it.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {(Object.keys(PROVIDER_CATEGORIES) as ProviderCategory[]).map((category) => {
          const categoryInfo = PROVIDER_CATEGORIES[category];
          const categoryProviders = getProvidersByCategory(category);
          
          return (
            <div key={category} className="space-y-3">
              <div className="flex items-center space-x-3 border-b border-border pb-2">
                <div className="bg-primary/10 p-1.5 rounded-full">
                  {categoryInfo.icon === "brain" && <Brain className="h-5 w-5" />}
                  {categoryInfo.icon === "speaker" && <Speaker className="h-5 w-5" />}
                  {categoryInfo.icon === "mic" && <Mic className="h-5 w-5" />}
                  {categoryInfo.icon === "phone" && <Phone className="h-5 w-5" />}
                  {categoryInfo.icon === "settings" && <Settings className="h-5 w-5" />}
                  {categoryInfo.icon === "zap" && <Zap className="h-5 w-5" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{categoryInfo.name}</h3>
                  <p className="text-sm text-muted-foreground">{categoryInfo.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryProviders.map((providerName) => {
                  const existingCredential = getExistingCredential(providerName);
                  const providerInfo = PROVIDER_INFO[providerName as ProviderName];

                  return (
                    <Card
                      key={providerName}
                      className={`gap-2 py-3 ${
                        existingCredential?.is_active === false ? "opacity-70" : ""
                      }`}
                    >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 pb-0">
                <div className="flex items-center space-x-2">
                  <div className="bg-primary/10 p-1.5 rounded-full">
                    {getProviderIcon(providerName)}
                  </div>
                  <CardTitle className="text-sm">{providerName}</CardTitle>
                </div>
                {existingCredential && (
                  <Switch
                    checked={existingCredential.is_active}
                    onCheckedChange={() =>
                      handleToggleStatus(existingCredential)
                    }
                  />
                )}
              </CardHeader>
              <CardContent className="px-4">
                <CardDescription className="line-clamp-2 text-xs">
                  {providerInfo?.description ||
                    `${providerName} API integration`}
                </CardDescription>
                <div className="mt-2">
                  {existingCredential ? (
                    <div className="flex items-center space-x-2">
                      <div className="bg-good-bg text-good text-xs px-2 py-1 rounded-full">
                        Configured
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Last updated:{" "}
                        {new Date(
                          existingCredential.updated_at,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-mid-bg text-mid text-xs px-2 py-1 rounded-full inline-block">
                      Not Configured
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between px-4 pt-0">
                {existingCredential ? (
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleOpenDialog(
                          providerName as ProviderName,
                          existingCredential,
                        )
                      }
                    >
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Delete {providerName} Credentials
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete these credentials?
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(existingCredential)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() =>
                      handleOpenDialog(providerName as ProviderName)
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Credentials
                  </Button>
                )}
                {providerInfo?.docsUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(providerInfo.docsUrl, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" /> Docs
                  </Button>
                )}
              </CardFooter>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Credential Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedCredential ? "Edit" : "Add"} {selectedProvider}{" "}
              Credentials
            </DialogTitle>
            <DialogDescription>
              {selectedCredential
                ? `Update your ${selectedProvider} API credentials`
                : `Enter your ${selectedProvider} API credentials to integrate with this service`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedProvider &&
              PROVIDER_INFO[selectedProvider].fields.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>{field.label}</Label>
                  {field.type === "select" ? (
                    <Select
                      value={formData[field.name] || ""}
                      onValueChange={(value) =>
                        handleInputChange(field.name, value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            field.placeholder || `Select ${field.label}`
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={field.name}
                      type={field.type}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ""}
                      onChange={(e) =>
                        handleInputChange(field.name, e.target.value)
                      }
                      required={field.required}
                    />
                  )}
                  {field.description && (
                    <p className="text-xs text-muted-foreground">
                      {field.description}
                    </p>
                  )}
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDialog}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
